import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Radio, Select, Space, Steps, Typography, message } from 'antd'
import type {
  OrdersImportAnalyzeData,
  OrdersImportPersonItem,
  OrdersImportPersonResolution,
  OrdersImportResolutions,
  OrdersImportStageResolution,
} from '../../../lib/import-upload'

const { Text, Paragraph, Title } = Typography

type PersonDraft = {
  name: string
  mode: 'create' | 'map'
  userId?: number
}

type StageDraft = {
  nameFromFile: string
  stageCode?: string
}

type Props = {
  analysis: OrdersImportAnalyzeData
  submitting?: boolean
  onCancel: () => void
  onCommit: (resolutions: OrdersImportResolutions) => void
}

function peopleNeedingResolution(items: OrdersImportPersonItem[]) {
  return items.filter(item => item.status !== 'ok')
}

export function OrdersImportWizard({ analysis, submitting, onCancel, onCommit }: Props) {
  const missingManagers = useMemo(
    () => peopleNeedingResolution(analysis.managers),
    [analysis.managers],
  )
  const missingMeasurers = useMemo(
    () => peopleNeedingResolution(analysis.measurers),
    [analysis.measurers],
  )
  const missingStages = useMemo(
    () => analysis.stages.filter(s => s.status !== 'ok'),
    [analysis.stages],
  )

  const hasPeopleStep = missingManagers.length + missingMeasurers.length > 0
  const hasStagesStep = missingStages.length > 0

  const stepKeys = useMemo(() => {
    const keys: Array<'people' | 'stages' | 'review'> = []
    if (hasPeopleStep) keys.push('people')
    if (hasStagesStep) keys.push('stages')
    keys.push('review')
    return keys
  }, [hasPeopleStep, hasStagesStep])

  const [stepIndex, setStepIndex] = useState(0)
  const currentKey = stepKeys[stepIndex] || 'review'

  const [managerDrafts, setManagerDrafts] = useState<PersonDraft[]>([])
  const [measurerDrafts, setMeasurerDrafts] = useState<PersonDraft[]>([])
  const [stageDrafts, setStageDrafts] = useState<StageDraft[]>([])

  useEffect(() => {
    setManagerDrafts(
      missingManagers.map(item =>
        item.status === 'not_in_office' && item.userId
          ? { name: item.name, mode: 'map' as const, userId: item.userId }
          : { name: item.name, mode: 'create' as const, userId: undefined },
      ),
    )
    setMeasurerDrafts(
      missingMeasurers.map(item =>
        item.status === 'not_in_office' && item.userId
          ? { name: item.name, mode: 'map' as const, userId: item.userId }
          : { name: item.name, mode: 'create' as const, userId: undefined },
      ),
    )
    setStageDrafts(
      missingStages.map(item => ({
        nameFromFile: item.name,
        stageCode: item.suggestedStageCode || undefined,
      })),
    )
    setStepIndex(0)
  }, [missingManagers, missingMeasurers, missingStages])

  const officeUserOptions = useMemo(
    () =>
      analysis.officeUsers.map(u => ({
        value: u.id,
        label: u.name ? `${u.name}${u.login ? ` (${u.login})` : ''}` : u.login || `ID ${u.id}`,
      })),
    [analysis.officeUsers],
  )

  const stageOptions = useMemo(
    () => analysis.orderStages.map(s => ({ value: s.code, label: s.name })),
    [analysis.orderStages],
  )

  const updatePersonDraft = (
    kind: 'manager' | 'measurer',
    name: string,
    patch: Partial<PersonDraft>,
  ) => {
    const setter = kind === 'manager' ? setManagerDrafts : setMeasurerDrafts
    setter(prev => prev.map(item => (item.name === name ? { ...item, ...patch } : item)))
  }

  const validatePeople = () => {
    const all = [...managerDrafts, ...measurerDrafts]
    for (const item of all) {
      if (item.mode === 'map' && !item.userId) {
        message.warning(`Выберите сотрудника для «${item.name}» или создайте нового`)
        return false
      }
    }
    return true
  }

  const validateStages = () => {
    for (const item of stageDrafts) {
      if (!item.stageCode) {
        message.warning(`Укажите стадию для «${item.nameFromFile}»`)
        return false
      }
    }
    return true
  }

  const buildResolutions = (): OrdersImportResolutions => {
    const toPersonRes = (items: PersonDraft[]): OrdersImportPersonResolution[] =>
      items.map(item =>
        item.mode === 'map'
          ? { name: item.name, action: 'map', userId: item.userId }
          : { name: item.name, action: 'create' },
      )

    const stages: OrdersImportStageResolution[] = stageDrafts
      .filter(s => s.stageCode)
      .map(s => ({ nameFromFile: s.nameFromFile, stageCode: s.stageCode! }))

    return {
      managers: toPersonRes(managerDrafts),
      measurers: toPersonRes(measurerDrafts),
      stages,
    }
  }

  const goNext = () => {
    if (currentKey === 'people' && !validatePeople()) return
    if (currentKey === 'stages' && !validateStages()) return
    if (stepIndex >= stepKeys.length - 1) {
      onCommit(buildResolutions())
      return
    }
    setStepIndex(i => i + 1)
  }

  const goBack = () => {
    if (stepIndex === 0) {
      onCancel()
      return
    }
    setStepIndex(i => i - 1)
  }

  const createCount =
    managerDrafts.filter(d => d.mode === 'create').length +
    measurerDrafts.filter(d => d.mode === 'create').length

  const stepItems = stepKeys.map(key => {
    if (key === 'people') return { title: 'Сотрудники' }
    if (key === 'stages') return { title: 'Стадии' }
    return { title: 'Проверка' }
  })

  const renderPersonBlock = (
    title: string,
    hint: string,
    drafts: PersonDraft[],
    kind: 'manager' | 'measurer',
  ) => {
    if (!drafts.length) return null
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Title level={5} style={{ marginBottom: 4 }}>
            {title}
          </Title>
          <Text type="secondary">{hint}</Text>
        </div>
        {drafts.map(item => (
          <div
            key={`${kind}-${item.name}`}
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: 12,
            }}
          >
            <Text strong>{item.name}</Text>
            <div style={{ marginTop: 8 }}>
              <Radio.Group
                value={item.mode}
                onChange={e =>
                  updatePersonDraft(kind, item.name, {
                    mode: e.target.value,
                    userId: e.target.value === 'create' ? undefined : item.userId,
                  })
                }
              >
                <Space direction="vertical">
                  <Radio value="create">Создать сотрудника (рекомендуется)</Radio>
                  <Radio value="map">Это уже есть в системе — выбрать</Radio>
                </Space>
              </Radio.Group>
            </div>
            {item.mode === 'create' && (
              <Paragraph type="secondary" style={{ margin: '8px 0 0' }}>
                Войти в систему он пока не сможет. Email и пароль можно задать позже в разделе
                сотрудников.
              </Paragraph>
            )}
            {item.mode === 'map' && (
              <Select
                style={{ width: '100%', marginTop: 8 }}
                placeholder="Выберите сотрудника офиса"
                options={officeUserOptions}
                value={item.userId}
                showSearch
                optionFilterProp="label"
                onChange={userId => updatePersonDraft(kind, item.name, { userId, mode: 'map' })}
              />
            )}
          </div>
        ))}
      </Space>
    )
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>
          Нужно уточнить данные перед импортом
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          В файле есть имена, которых система ещё не знает. Пройдите короткие шаги — это займёт
          минуту.
        </Paragraph>
      </div>

      <Steps size="small" current={stepIndex} items={stepItems} />

      {currentKey === 'people' && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Сотрудники из файла"
            description="Если человека ещё нет в системе — оставьте «Создать». Если ФИО написано иначе, чем в системе — выберите существующего сотрудника."
          />
          {renderPersonBlock(
            'Менеджеры',
            'Указаны в колонке «Менеджер».',
            managerDrafts,
            'manager',
          )}
          {renderPersonBlock(
            'Ответственные за замер',
            'Указаны в колонке «Отв за замер».',
            measurerDrafts,
            'measurer',
          )}
        </Space>
      )}

      {currentKey === 'stages' && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Стадии заказов"
            description="В файле стадии названы не так, как в системе. Для каждого названия укажите, какой стадии оно соответствует."
          />
          {stageDrafts.map(item => (
            <div key={item.nameFromFile}>
              <Text>
                В файле написано: <Text strong>«{item.nameFromFile}»</Text>
              </Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                placeholder="Какой стадии это соответствует?"
                options={stageOptions}
                value={item.stageCode}
                onChange={stageCode =>
                  setStageDrafts(prev =>
                    prev.map(s =>
                      s.nameFromFile === item.nameFromFile ? { ...s, stageCode } : s,
                    ),
                  )
                }
              />
            </div>
          ))}
        </Space>
      )}

      {currentKey === 'review' && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="success"
            showIcon
            message="Почти готово"
            description="Проверьте кратко, что сделаем, и нажмите «Импортировать»."
          />
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>
              Создадим сотрудников без входа в систему: <Text strong>{createCount}</Text>
            </li>
            <li>
              Сопоставим названия стадий: <Text strong>{stageDrafts.length}</Text>
            </li>
            <li>
              Импортируем заказов (строк без ошибок в данных):{' '}
              <Text strong>{analysis.stats.importableRowCount}</Text> из {analysis.stats.rowCount}
            </li>
          </ul>
          {analysis.rowHardErrors.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`В файле ${analysis.rowHardErrors.length} строк(и) с ошибками данных`}
              description={
                <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                  {analysis.rowHardErrors.slice(0, 8).map(err => (
                    <li key={err.rowNumber}>
                      Строка {err.rowNumber}: {err.messages.join('; ')}
                    </li>
                  ))}
                  {analysis.rowHardErrors.length > 8 && (
                    <li>…и ещё {analysis.rowHardErrors.length - 8}</li>
                  )}
                </ul>
              }
            />
          )}
        </Space>
      )}

      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Button onClick={goBack} disabled={submitting}>
          {stepIndex === 0 ? 'Отмена' : 'Назад'}
        </Button>
        <Button type="primary" onClick={goNext} loading={submitting}>
          {currentKey === 'review' ? 'Импортировать' : 'Продолжить'}
        </Button>
      </Space>
    </Space>
  )
}

export default OrdersImportWizard
